import { TAG_COLORS } from '@OpsWb/constants'

import colors from '~/styles/_colors.css'
import {
    JobProductionCountryFilter,
    OrderType,
    ProductionCountry,
    ProductionCountryFilter,
    SchedulingTypes,
} from '~/gql/graphql'

import { TagType } from './types'

export const MAX_PROJECT_NAME_LENGTH = 20

export const PRODUCTION_TABS = {
    salesOrders: 'salesOrders',
    jobRequests: 'jobRequests',
    exceptions: 'exceptions',
    cars: 'cars',
    jobs: 'jobs',
    unbundledProducts: 'unbundledProducts',
} as const
export const TABS_NAME = {
    cars: 'CARs',
    exceptions: 'Exceptions',
    jobRequests: 'Job requests',
    salesOrders: 'Sales orders',
    jobs: 'Jobs',
    unbundledProducts: 'Unbundled products',
}
export const SHORT_TIME_FORMAT = 'hh:mm a'
export const DATE_FORMAT_YYYY_MM_DD = 'YYYY-MM-DD'
export const YEAR_FIRST_DATE_FORMAT = 'YYYY-MM-DD, HH:mm z'
export const YEAR_FIRST_DATE_WITHOUT_TIMEZONE_FORMAT = 'YYYY-MM-DD, HH:mm'

export const SalesOrderTopSearchOptions = [
    { value: `${PRODUCTION_TABS.salesOrders}_salesOrder`, name: 'Sales order' },
    { value: `${PRODUCTION_TABS.salesOrders}_jobRequest`, name: 'Job request' },
    { value: `${PRODUCTION_TABS.salesOrders}_project`, name: 'Project' },
]
export const ExceptionTopSearchOptions = [
    { value: `${PRODUCTION_TABS.exceptions}_salesOrder`, name: 'Sales order' },
    { value: `${PRODUCTION_TABS.exceptions}_jobRequest`, name: 'Job name' },
    { value: `${PRODUCTION_TABS.exceptions}_mp`, name: 'MP' },
    { value: `${PRODUCTION_TABS.exceptions}_exception`, name: 'Exception ID' },
]

export const JobRequestTopSearchOptions = [
    { value: `${PRODUCTION_TABS.jobRequests}_salesOrder`, name: 'Sales order' },
    { value: `${PRODUCTION_TABS.jobRequests}_jobRequest`, name: 'Job request' },
    { value: `${PRODUCTION_TABS.jobRequests}_mp`, name: 'MP name' },
    { value: `${PRODUCTION_TABS.jobRequests}_project`, name: 'Project' },
]

export const carsTopSearchOptions = [
    { value: `${PRODUCTION_TABS.cars}_salesOrder`, name: 'Sales order' },
    { value: `${PRODUCTION_TABS.cars}_carId`, name: 'CAR ID' },
    { value: `${PRODUCTION_TABS.cars}_exceptionId`, name: 'Exception ID' },
    { value: `${PRODUCTION_TABS.cars}_jobName`, name: 'Job Name' },
    { value: `${PRODUCTION_TABS.cars}_mpAssignee`, name: 'MP Assignee' },
]

export const jobsTopSearchOptions = [
    { value: `${PRODUCTION_TABS.jobs}_jobName`, name: 'Job name' },
    { value: `${PRODUCTION_TABS.jobs}_salesOrder`, name: 'Sales order' },
    { value: `${PRODUCTION_TABS.jobs}_project`, name: 'Project name' },
    {
        value: `${PRODUCTION_TABS.jobs}_customerCompany`,
        name: 'Customer company',
    },
    { value: `${PRODUCTION_TABS.jobs}_customerName`, name: 'Customer name' },
]

export const unbundledProductsTopSearchOptions = [
    {
        value: `${PRODUCTION_TABS.unbundledProducts}_salesOrder`,
        name: 'Sales order',
    },
    {
        value: `${PRODUCTION_TABS.unbundledProducts}_project`,
        name: 'Project name',
    },
]

export const ProductionWbTabOptions = {
    [PRODUCTION_TABS.salesOrders]: {
        defaultIndex: SalesOrderTopSearchOptions.findIndex(
            option =>
                option.value === `${PRODUCTION_TABS.salesOrders}_salesOrder`,
        ),
        options: SalesOrderTopSearchOptions,
    },
    [PRODUCTION_TABS.jobRequests]: {
        defaultIndex: JobRequestTopSearchOptions.findIndex(
            option =>
                option.value === `${PRODUCTION_TABS.jobRequests}_jobRequest`,
        ),
        options: JobRequestTopSearchOptions,
    },
    [PRODUCTION_TABS.exceptions]: {
        defaultIndex: ExceptionTopSearchOptions.findIndex(
            option =>
                option.value === `${PRODUCTION_TABS.exceptions}_exception`,
        ),
        options: ExceptionTopSearchOptions,
    },
    [PRODUCTION_TABS.cars]: {
        defaultIndex: carsTopSearchOptions.findIndex(
            option => option.value === `${PRODUCTION_TABS.cars}_salesOrder`,
        ),
        options: carsTopSearchOptions,
    },
    [PRODUCTION_TABS.jobs]: {
        defaultIndex: jobsTopSearchOptions.findIndex(
            option => option.value === `${PRODUCTION_TABS.jobs}_jobName`,
        ),
        options: jobsTopSearchOptions,
    },
    [PRODUCTION_TABS.unbundledProducts]: {
        defaultIndex: unbundledProductsTopSearchOptions.findIndex(
            option =>
                option.value ===
                `${PRODUCTION_TABS.unbundledProducts}_salesOrder`,
        ),
        options: unbundledProductsTopSearchOptions,
    },
} as const

export const JOB_REQUEST_STATUS_FILTERS = [
    { LABEL: 'Closed', VALUE: 'Closed' },
    { LABEL: 'Accepted', VALUE: 'Accepted' },
    { LABEL: 'Secondary processing', VALUE: 'Secondary processing' },
    { LABEL: 'Inspection submitted', VALUE: 'Inspection submitted' },
] as const

export const PRIORITY_FILTERS = [
    { LABEL: 'Very Urgent', VALUE: 'very_urgent' },
    { LABEL: 'Urgent', VALUE: 'urgent' },
    { LABEL: 'Normal', VALUE: 'normal' },
    { LABEL: 'Not Urgent', VALUE: 'not_urgent' },
] as const

export const REGION_FILTERS = [
    { LABEL: 'Domestic', VALUE: 'domestic' },
    { LABEL: 'Overseas', VALUE: 'overseas' },
    { LABEL: 'Multiple', VALUE: 'multiple' },
] as const

export const EXCEPTION_STATUS_FILTERS = [
    { LABEL: 'Open exception', VALUE: 'Open exception' },
    { LABEL: 'Open failure', VALUE: 'Open failure' },
    { LABEL: 'Closed exception', VALUE: 'Closed exception' },
    { LABEL: 'Closed failure', VALUE: 'Closed failure' },
    { LABEL: 'Voided exception', VALUE: 'Voided exception' },
] as const

export const JOB_STATUS_FILTERS = [
    { LABEL: 'Accepted', VALUE: 'accepted' },
    { LABEL: 'Cancelled', VALUE: 'canceled' },
    { LABEL: 'Completed', VALUE: 'completed' },
    { LABEL: 'In primary processing', VALUE: 'in_primary_processing' },
    { LABEL: 'In secondary processing', VALUE: 'in_secondary_processing' },
    { LABEL: 'In inspection', VALUE: 'in_inspection' },
    { LABEL: 'Inspection submitted', VALUE: 'inspection_submitted' },
    { LABEL: 'Unaccepted', VALUE: 'unaccepted' },
]

export const PRODUCTION_COUNTRIES_FILTER_SALEORDER = [
    {
        LABEL: ProductionCountryFilter.IN,
        VALUE: ProductionCountryFilter.IN,
    },
    {
        LABEL: ProductionCountryFilter.CN,
        VALUE: ProductionCountryFilter.CN,
    },
    {
        LABEL: ProductionCountryFilter.MX,
        VALUE: ProductionCountryFilter.MX,
    },
    {
        LABEL: ProductionCountryFilter.US,
        VALUE: ProductionCountryFilter.US,
    },
    {
        LABEL: ProductionCountryFilter.Unassigned,
        VALUE: ProductionCountryFilter.Unassigned,
    },
]

export const ASSIGNEE_FILTERS = [
    { LABEL: 'MP', VALUE: 'mp' },
    { LABEL: 'TCS', VALUE: 'tcs' },
    { LABEL: 'Fulfillment', VALUE: 'fulfillment' },
    { LABEL: 'Quality', VALUE: 'quality' },
    { LABEL: 'Logistics', VALUE: 'logistics' },
] as const

export const JOB_PRODUCTION_COUNTRIES_FILTERS = [
    {
        label: JobProductionCountryFilter.IN,
        value: JobProductionCountryFilter.IN,
    },
    {
        label: JobProductionCountryFilter.CN,
        value: JobProductionCountryFilter.CN,
    },
    {
        label: JobProductionCountryFilter.MX,
        value: JobProductionCountryFilter.MX,
    },
    {
        label: JobProductionCountryFilter.US,
        value: JobProductionCountryFilter.US,
    },
    {
        label: JobProductionCountryFilter.Unassigned,
        value: JobProductionCountryFilter.Unassigned,
    },
]

export const AssigneeMapping: {
    [key: string]: { text: string }
} = {
    mp: { text: 'MP' },
    tcs: { text: 'TCS' },
    quality: { text: 'Quality' },
    fulfillment: { text: 'Fulfillment' },
    logistics: { text: 'Logistics' },
} as const

export const EXCEPTION_STATUS = {
    OPEN_FAILURE: 'Open failure',
    CLOSED_FAILURE: 'Closed failure',
    OPEN_EXCEPTION: 'Open exception',
    CLOSED_EXCEPTION: 'Closed exception',
    VOIDED_EXCEPTION: 'Voided exception',
} as const

export const FAILURE = {
    MP: 'MP',
    FICTIV: 'FICTIV',
    CUSTOMER_AFFECTED: 'Customer Affected',
} as const

export const ActionModalMapping: {
    [key: string]: { actionName: string; msg: string }
} = {
    open: {
        actionName: 'Reopen exception',
        msg: 'Are you sure you want to reopen this exception?',
    },
    closed: {
        actionName: 'Close exception',
        msg: 'Are you sure you want to close this exception? To make edits you will need to reopen exception.',
    },
    voided: {
        actionName: 'Void exception',
        msg: 'Are you sure you want to void this exception? This action cannot be undone.',
    },
} as const

export const ExceptionTagMap: {
    [key: string]: { color: string; title: string }
} = {
    [EXCEPTION_STATUS.OPEN_EXCEPTION]: {
        color: TAG_COLORS.BLUE,
        title: EXCEPTION_STATUS.OPEN_EXCEPTION,
    },
    [EXCEPTION_STATUS.OPEN_FAILURE]: {
        color: TAG_COLORS.RED,
        title: EXCEPTION_STATUS.OPEN_FAILURE,
    },
    [EXCEPTION_STATUS.CLOSED_FAILURE]: {
        color: colors.gray2,
        title: EXCEPTION_STATUS.CLOSED_FAILURE,
    },
    [EXCEPTION_STATUS.VOIDED_EXCEPTION]: {
        color: colors.gray2,
        title: EXCEPTION_STATUS.VOIDED_EXCEPTION,
    },
    [EXCEPTION_STATUS.CLOSED_EXCEPTION]: {
        color: TAG_COLORS.GREEN,
        title: EXCEPTION_STATUS.CLOSED_EXCEPTION,
    },
}

export const ProductionTopSearchfilterKeys = Object.freeze({
    MP: 'mp',
    PROJECT: 'project',
})

export const DEFAULT_PAGE_NUMBER = 1
export const LISTS_PAGINATION_LIMIT = 40

export const EXCEPTION_MODES = { OTIF: 'otif', EXCEPTION: 'exception' } as const

export const EXCEPTION_CAR_TYPE = { FICTIV: 'Fictiv', MP: 'MP' }

export const CAR_STATUSES = {
    PENDING: 'Pending',
    CANCELED: 'Canceled',
    CLOSED: 'Closed',
    SUBMITTED: 'Submitted',
}

export const CAR_FILTERS = [
    { LABEL: 'Fictiv - Pending', VALUE: 'fictiv-pending' },
    { LABEL: 'Fictiv - In Progress', VALUE: 'fictiv-in_progress' },
    { LABEL: 'Fictiv - Submitted', VALUE: 'fictiv-submitted' },
    { LABEL: 'Fictiv - Returned for revision', VALUE: 'fictiv-rework' },
    { LABEL: 'Fictiv - Approved', VALUE: 'fictiv-approved' },
    { LABEL: 'Fictiv - Closed', VALUE: 'fictiv-closed' },
    { LABEL: 'Fictiv - Canceled', VALUE: 'fictiv-canceled' },
    { LABEL: 'MP - Pending', VALUE: 'mp-pending' },
    { LABEL: 'MP - In Progress', VALUE: 'mp-in_progress' },
    { LABEL: 'MP - Submitted', VALUE: 'mp-submitted' },
    { LABEL: 'MP - Returned for revision', VALUE: 'mp-rework' },
    { LABEL: 'MP - Approved', VALUE: 'mp-approved' },
    { LABEL: 'MP - Closed', VALUE: 'mp-closed' },
    { LABEL: 'MP - Canceled', VALUE: 'mp-canceled' },
] as const

export const DiscoveryMapping: {
    [key: string]: string
} = {
    customer: 'Customer',
    mp: 'MP',
    qc_feidi: 'Quality - Feidi',
    qc_hq: 'Quality - HQ',
    qc_india: 'Quality - India',
    mp_mfg_dri: 'MP Mfg DRI',
} as const

export const MAX_NAME_LENGTH = 21
export const FILTER_OPTIONS_ERROR =
    'Error in retrieving filters. Please refresh page.'

export const SORT_ORDERS = { ascending: 'ASC', descending: 'DESC' }

export const SORT_TYPES = {
    unbundledPartPayout: 'UNBUNDLED_PART_PAYOUT',
    jobPayout: 'PAYOUT',
    scheduleDate: 'EARLIST_SCHEDULED_DATE',
    priority: 'PRIORITY',
    jobDueDate: 'JOB_DUE_DATE',
    leadTime: 'LEAD_TIME',
}

export const JOB_REQUEST_STATUS = {
    ACCEPTED: 'Accepted',
    INSPECTION_SUBMITTED: 'Inspection submitted',
    MARKED_AS_SHIPPED: 'Marked as shipped',
    PRIMARY_PROCESSING: 'In primary processing',
    CLOSED: 'Closed',
    SECONDARY_PROCESSING: 'Secondary processing',
}

export const JOB_STATUS = {
    UNACCEPTED: 'unaccepted',
    CANCELED: 'canceled',
    COMPLETED: 'completed',
    ACCEPTED: 'accepted',
    IN_PRIMARY_PROCESSING: 'in_primary_processing',
    IN_SECONDARY_PROCESSING: 'in_secondary_processing',
    IN_INSPECTION: 'in_inspection',
    INSPECTION_SUBMITTED: 'inspection_submitted',
} as const

export const JobStatusTagList: TagType = {
    [JOB_STATUS.UNACCEPTED]: {
        color: TAG_COLORS.ORANGE,
        title: 'Unaccepted',
    },
    [JOB_STATUS.CANCELED]: {
        color: '',
        title: 'Cancelled',
    },
    [JOB_STATUS.COMPLETED]: {
        color: '',
        title: 'Completed',
    },
    [JOB_STATUS.ACCEPTED]: {
        color: TAG_COLORS.GREEN,
        title: 'Accepted',
    },
    [JOB_STATUS.IN_PRIMARY_PROCESSING]: {
        color: TAG_COLORS.BLUE,
        title: 'In primary processing',
    },
    [JOB_STATUS.IN_SECONDARY_PROCESSING]: {
        color: TAG_COLORS.BLUE,
        title: 'In secondary processing',
    },
    [JOB_STATUS.IN_INSPECTION]: {
        color: TAG_COLORS.BLUE,
        title: 'In inspection',
    },
    [JOB_STATUS.INSPECTION_SUBMITTED]: {
        color: TAG_COLORS.GREEN,
        title: 'Inspection submitted',
    },
}

export const JobStatusTagMap: TagType = {
    [JOB_STATUS.UNACCEPTED]: {
        color: TAG_COLORS.RED,
        title: 'Unaccepted',
    },
    [JOB_STATUS.CANCELED]: {
        color: '',
        title: 'Cancelled',
    },
    [JOB_STATUS.COMPLETED]: {
        color: '',
        title: 'Completed',
    },
    [JOB_STATUS.ACCEPTED]: {
        color: TAG_COLORS.GREEN,
        title: 'Accepted',
    },
}

export const DATE_FORMATS = {
    YYYY_MM_DD_WITH_SLASH: 'YYYY/DD/MM HH:mm z',
    YEAR_FIRST_DATE_FORMAT: 'YYYY-MM-DD, HH:mm z',
    YEAR_FIRST_DATE_WITHOUT_COMMA_SEP_FORMAT: 'YYYY-MM-DD HH:mm z',
    MONTH_FIRST_DATE_FORMAT: 'MM/DD/YYYY HH:mm z',
    YEAR_FIRST_DATE_WITHOUT_TIMEZONE_SECONDS: 'YYYY-MM-DD HH:mm',
}

export const TIME_FORMATS = {
    HH_mm_ss_a: 'HH:mm:ss a',
}

export const MILESTONE_ON_TRACK_COLOR = 'green'
export const MILESTONE_OFF_TRACK_COLOR = 'orange'

export const MILESTONE_LIST = [
    { label: 'Schedule MP by', value: 'scheduling' },
    { label: 'Fictiv dock date', value: 'production' },
    { label: 'Fictiv QC by', value: 'inspection' },
    { label: 'Shipment release by', value: 'delivery_order' },
    { label: 'Carrier pickup by', value: 'shipping' },
    { label: 'Customer delivery date', value: 'customer_delivery' },
]

export const ERROR_DURATION = 3
export const ORDER_TYPE_MAPPING = {
    [OrderType.high_touch]: 'High touch',
    [OrderType.self_serve]: 'Self serve',
}
export const ORDER_TYPE_FILTERS = [
    {
        label: ORDER_TYPE_MAPPING[OrderType.high_touch],
        value: OrderType.high_touch,
    },
    {
        label: ORDER_TYPE_MAPPING[OrderType.self_serve],
        value: OrderType.self_serve,
    },
]

export const PRODUCTION_COUNTRY = [
    {
        label: ProductionCountry.IN,
        value: ProductionCountry.IN,
    },
    {
        label: ProductionCountry.CN,
        value: ProductionCountry.CN,
    },
    {
        label: ProductionCountry.MX,
        value: ProductionCountry.MX,
    },
    {
        label: ProductionCountry.US,
        value: ProductionCountry.US,
    },
]

export const PRODUCTION_COUNTRIES_FILTERS = [
    {
        label: ProductionCountryFilter.IN,
        value: ProductionCountryFilter.IN,
    },
    {
        label: ProductionCountryFilter.CN,
        value: ProductionCountryFilter.CN,
    },
    {
        label: ProductionCountryFilter.MX,
        value: ProductionCountryFilter.MX,
    },
    {
        label: ProductionCountryFilter.US,
        value: ProductionCountryFilter.US,
    },
    {
        label: ProductionCountryFilter.Unassigned,
        value: ProductionCountryFilter.Unassigned,
    },
]

export const DEFAULT_PROD_COUNTRY = 'Unassigned'

export const SHCEDULING_TYPES_TITLES = {
    MANUAL: 'Manual',
    JOB_BOARD_LIVE: 'Job board (Live)',
    JOB_BOARD_NEXT: 'Job board (Next)',
    AUTO_SCHEDULING: 'Autoscheduling',
}

export const SCHEDULING_TYPES_MAPPING: { [k: string]: string } = {
    [SchedulingTypes.MANUAL]: SHCEDULING_TYPES_TITLES.MANUAL,
    [`${SchedulingTypes.JOB_BOARD}_LIVE`]:
        SHCEDULING_TYPES_TITLES.JOB_BOARD_LIVE,
    [`${SchedulingTypes.JOB_BOARD}_NEXT`]:
        SHCEDULING_TYPES_TITLES.JOB_BOARD_NEXT,
    [SchedulingTypes.AUTO_SCHEDULE]: SHCEDULING_TYPES_TITLES.AUTO_SCHEDULING,
}

export const SCHEDULING_TYPES_VALUE_MAPPING: { [k: string]: string } = {
    [SHCEDULING_TYPES_TITLES.MANUAL]: SchedulingTypes.MANUAL,
    [SHCEDULING_TYPES_TITLES.JOB_BOARD_LIVE]: SchedulingTypes.JOB_BOARD,
    [SHCEDULING_TYPES_TITLES.JOB_BOARD_NEXT]: SchedulingTypes.JOB_BOARD,
    [SHCEDULING_TYPES_TITLES.AUTO_SCHEDULING]: SchedulingTypes.AUTO_SCHEDULE,
}
